import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import PropertyFilter from '../Shared/PropertyFilter';
import MorningBrief from './MorningBrief';
import FlightRisk from './FlightRisk';
import SynergyMap from './SynergyMap';
import DigitalTwin from './DigitalTwin';
import PerformanceGaps from './PerformanceGaps';
import SalesVelocity from './SalesVelocity';
import ActiveAlerts from './ActiveAlerts';
import './command-center.css';

/**
 * CommandCenter — Sprint 3 E1 top-level page.
 *
 * Fetches /api/command/:concessionaireId once on mount (and when the property
 * filter or refresh button changes) and fans the 7 sections out to children.
 */
export default function CommandCenter() {
    const { t } = useTranslation();
    const { concessionaireId } = useAuth();
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!concessionaireId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const params = selectedProperties.length ? { propertyId: selectedProperties[0] } : undefined;
                const res = await v3Api.command.get(concessionaireId, params);
                if (!cancelled) setPayload(res);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.error || e.message || t('Failed to load command center'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [concessionaireId, selectedProperties, reloadKey]);

    const refresh = useCallback(() => setReloadKey((k) => k + 1), []);
    const data = payload?.data || {};
    const meta = payload?.meta || {};

    // For DigitalTwin, prefer the active property; else fall back to the first
    // concrete property the user has access to.
    const twinPropertyId = selectedProperties[0] || meta.property_id || '';

    return (
        <div className="v3 v3-cc">
            <div className="v3-cc-header">
                <div>
                    <h1 className="v3-cc-title">{t('Command Center')}</h1>
                    <div className="v3-cc-subtitle">
                        {t('Operator-grade overview')} · {meta.cache_status === 'cached' ? t('cached') : t('live')}
                        {meta.generated_at && ` · ${new Date(meta.generated_at).toLocaleTimeString()}`}
                    </div>
                </div>
                <div className="v3-cc-actions">
                    <PropertyFilter value={selectedProperties} onChange={setSelectedProperties} />
                    <button className="v3-btn v3-btn-ghost" onClick={refresh} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'spin' : ''} />
                        {t('Refresh')}
                    </button>
                </div>
            </div>

            {loading && !payload && (
                <div className="v3-card v3-cc-card v3-cc-loading">{t('Loading Command Center…')}</div>
            )}

            {error && (
                <div className="v3-card v3-cc-card v3-cc-error v3-text-danger">
                    {error}
                </div>
            )}

            {payload && (
                <div className="v3-cc-grid">
                    <div className="cc-col-12">
                        <MorningBrief brief={data.morningBrief} />
                    </div>
                    <div className="cc-col-6">
                        <FlightRisk rows={data.flightRisk} />
                    </div>
                    <div className="cc-col-6">
                        <ActiveAlerts alerts={data.activeAlerts} />
                    </div>
                    <div className="cc-col-6">
                        <SynergyMap map={data.synergyMap} />
                    </div>
                    <div className="cc-col-6">
                        <SalesVelocity velocity={data.salesVelocity} />
                    </div>
                    <div className="cc-col-7">
                        <PerformanceGaps gaps={data.performanceGaps} />
                    </div>
                    <div className="cc-col-5">
                        <DigitalTwin propertyId={twinPropertyId} />
                    </div>
                </div>
            )}
        </div>
    );
}
